'use client';

import { useEffect } from 'react';
import Icon from './AppIcon';

type ToastVariant = 'success' | 'error' | 'info' | 'warning';

interface ToastProps {
  id: string;
  message: string;
  variant: ToastVariant;
  duration?: number;
  onClose: (id: string) => void;
}

const toastConfig = {
  success: {
    icon: 'CheckCircleIcon' as const,
    bgColor: '#22C55E18',
    borderColor: '#22C55E',
    iconColor: '#22C55E',
  },
  error: {
    icon: 'XCircleIcon' as const,
    bgColor: '#DC262618',
    borderColor: '#DC2626',
    iconColor: '#DC2626',
  },
  info: {
    icon: 'InformationCircleIcon' as const,
    bgColor: '#3B82F618',
    borderColor: '#3B82F6',
    iconColor: '#3B82F6',
  },
  warning: {
    icon: 'ExclamationTriangleIcon' as const,
    bgColor: '#F59E0B18',
    borderColor: '#F59E0B',
    iconColor: '#F59E0B',
  },
};

export default function Toast({ id, message, variant, duration = 5000, onClose }: ToastProps) {
  const config = toastConfig[variant];

  useEffect(() => {
    const timer = setTimeout(() => {
      onClose(id);
    }, duration);

    return () => clearTimeout(timer);
  }, [id, duration, onClose]);

  return (
    <div
      className="flex items-start gap-3 p-4 rounded-xl shadow-lg min-w-[320px] max-w-md animate-slide-in"
      style={{
        background: config.bgColor,
        border: `1px solid ${config.borderColor}`,
      }}
    >
      <Icon
        name={config.icon}
        variant="solid"
        size={24}
        style={{ color: config.iconColor }}
        className="flex-shrink-0 mt-0.5"
      />
      <p
        className="flex-1 text-sm leading-relaxed"
        style={{ color: 'var(--text-primary)' }}
      >
        {message}
      </p>
      <button
        onClick={() => onClose(id)}
        className="flex-shrink-0 hover:opacity-70 transition-opacity"
        aria-label="Close notification"
      >
        <Icon
          name="XMarkIcon"
          size={20}
          style={{ color: 'var(--text-secondary)' }}
        />
      </button>
    </div>
  );
}

export function ToastContainer({ toasts, onClose }: { toasts: ToastProps[]; onClose: (id: string) => void }) {
  return (
    <div className="fixed top-20 right-6 z-50 flex flex-col gap-3 pointer-events-none">
      <div className="pointer-events-auto flex flex-col gap-3">
        {toasts.map((toast) => (
          <Toast key={toast.id} {...toast} onClose={onClose} />
        ))}
      </div>
    </div>
  );
}